import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { IChatPoll } from "@/types/(my-space)/chat.interface";
import { ChatAvatar } from "./ChatAvatar";
import { pollVoteGroups } from "../_lib/poll-votes";

/**
 * Who voted what: every option in order with its count, then its voters (me
 * first). Reads `poll` as given, so it follows `message_updated` while open.
 */
export const PollVotesDialog = ({
  open,
  onOpenChange,
  poll,
  myUserId,
  memberNames,
  memberImages,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  poll: IChatPoll;
  myUserId: number | undefined;
  memberNames: Map<number, string>;
  memberImages?: Map<number, string | null>;
}) => {
  const { t } = useTranslation("chat");
  const groups = pollVoteGroups(poll, myUserId);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] flex-col sm:max-w-md">
        <DialogHeader>
          {/* Poll text is the sender's language, not the UI's: isolate its direction. */}
          <DialogTitle dir="auto" className="break-words">
            {poll.question}
          </DialogTitle>
          <DialogDescription>{t("poll.votes", { count: poll.totalVoters })}</DialogDescription>
        </DialogHeader>
        <div className="-mx-1 flex min-h-0 flex-col gap-4 overflow-y-auto px-1">
          {groups.map((g) => (
            <section key={g.id} className="flex flex-col gap-1">
              <h3 className="flex items-start justify-between gap-3 border-b border-border pb-1.5">
                <span
                  dir="auto"
                  className="min-w-0 text-sm font-semibold break-words text-foreground"
                >
                  {g.text}
                </span>
                <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
                  {g.voteCount > 0 ? t("poll.votes", { count: g.voteCount }) : t("poll.no_votes")}
                </span>
              </h3>
              {g.voterIds.length ? (
                <ul>
                  {g.voterIds.map((id) => {
                    const realName = memberNames.get(id);
                    const label =
                      id === myUserId ? t("poll.you") : (realName ?? t("system.someone"));
                    return (
                      <li key={id} className="flex items-center gap-3 py-1.5">
                        <ChatAvatar
                          name={realName ?? label}
                          imageUrl={memberImages?.get(id)}
                          size="sm"
                        />
                        <span className="truncate text-sm text-foreground">{label}</span>
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </section>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};
